import { ChildBridge as Bridge, BridgeConfig } from '@smartmediatech/smt-base-bridge';
import RequestMessage from '@smartmediatech/smt-base-bridge/src/model/request-message';
import { Subscription } from 'rxjs';
import jwtDecode from 'jwt-decode';
import { PlatformDatabase, getDatabase } from './database';
import AppletError, { DefaultErrors } from '../model/error';
import checkJwtToken from './network/check-jwt-expiration';
import PlatformSocket, { MessageType } from './network/platform-socket';
import getAccessToken from './network/get-access-token';
import refreshAccessToken from './network/refresh-access-token';
import Log from '../util/log';
import ActionQueue from './util/action-queue';
import checkAllowedDomain from './util/check-allowed-domain';

type RequestData = {
  endpoint: string;
  method: string;
  headers: Record<string, string>;
  body: string | undefined;
  isFormData: boolean;
  auth: boolean;
};
const viewerApi = [
  'https://api.vl.vatom.network',
  '.smartmediatech.io',
  'https://b.smartmedialabs.io',
  'https://s.smartmedialabs.io',
  'https://g.smartmedialabs.io',
];
const cluApi = [
  'https://cpl.smartmedialabs.io',
  'https://cpl-dev.smartmedialabs.io',
  'https://cpl-b.smartmedialabs.io',
  'https://cpl-s.smartmedialabs.io',
  'https://cpl-g.smartmedialabs.io',
  'https://cpl-l.smartmedialabs.io',
  'https://cpl-dev-b.smartmedialabs.io',
  'https://cpl-dev-s.smartmedialabs.io',
  'https://cpl-dev-g.smartmedialabs.io',
  'https://cpl-dev-l.smartmedialabs.io',
  'https://api.smartmedialabs.io',
];

const isViewer = (path: string) =>
  viewerApi.findIndex((api: string): boolean => path.indexOf(api) >= 0) >= 0;

const isClu = (path: string) =>
  cluApi.findIndex((api: string): boolean => path.indexOf(api) >= 0) >= 0;

class Requestor {
  private bridge: Bridge;

  private isInitialized = false;

  private allowedReferrer: string[] = [
    'smartwallet.app',
    'nftviewer.app',
    'smartmediawallet.app',
    'olympicrewards.com',
  ];

  private allowedRequest: string[] = [
    'api.vl.vatom.network',
    '.smartmedialabs.io',
    '.smartmediatech.io',
  ];

  private allowedWebsocket: string[] = ['ws.vatomviewer.com'];

  private websockets: Record<string, { ws: PlatformSocket; subscription: Subscription }> = {};

  private restrictedRequestPaths: string[] = ['/v1/access_token'];

  private appId?: string = undefined;

  private platformUrl?: string = undefined;

  private actionQueue: ActionQueue = new ActionQueue();

  private customRequestModifier: Array<{
    test: (path: string) => boolean;
    map: (db: PlatformDatabase, request: RequestData) => Promise<RequestData>;
  }> = [];

  private customResponseModifier: Array<{
    test: (path: string) => boolean;
    map: (
      db: PlatformDatabase,
      response: Record<string, any>,
      status: number,
    ) => Promise<Record<string, any>>;
  }> = [];

  constructor() {
    const referrer = new URL(document.referrer);
    if (!this.validateReferrer(referrer)) {
      throw new Error('Invalid referrer');
    }

    const bridge = new Bridge({
      meta: {
        version: '1.0.0',
        source: 'smt-platform-requestor',
      },
      origin: referrer.origin,
    } as BridgeConfig);
    this.bridge = bridge;
    Log.info('setting up request applet');
    getDatabase('smt-platform-requestor')
      .then((db) => this.init(db))
      .catch((error: Error) => {
        let appletError: AppletError = AppletError.createPlatformError({
          code: 401,
          status: 401,
          request_id: '',
          message: error.message,
        });
        if (error instanceof AppletError) {
          appletError = error;
        }
        Log.warn(error);
        this.bridge.sendRequest('init.complete', appletError.toJson());
      });
  }

  private async init(db: PlatformDatabase): Promise<void> {
    const referrer = new URL(document.referrer);
    if (this.validateReferrer(referrer)) {
      const referrerCampaign = referrer.hostname.split('.')[0];
      const campaign = window.location.hostname.split('.')[0];
      if (referrerCampaign !== campaign) {
        throw new Error('Campaigns need to match');
      }
    } else {
      throw new Error('Invalid referrer');
    }
    this.customRequestModifier = [
      {
        test: (path: string) => isViewer(path),
        map: (database: PlatformDatabase, request: RequestData): Promise<RequestData> => {
          const { headers } = request;
          headers['App-Id'] = this.appId ?? '';
          return Promise.resolve({ ...request, headers });
        },
      },
      {
        test: (path: string) => isClu(path) && path.indexOf('/signup') < 0,
        map: async (database: PlatformDatabase, request: RequestData): Promise<RequestData> => {
          const { body, isFormData } = request;
          let outBody = body;
          if (body && !isFormData) {
            const refreshToken: string | undefined = await database.config
              .findOne(this.appId)
              .exec()
              .then((document) => document?.get('refreshToken') as string | undefined);

            if (refreshToken) {
              const jsonBody = JSON.parse(body) as Record<string, any>;
              jsonBody.session_token = refreshToken;
              outBody = JSON.stringify(jsonBody);
            }
          }
          return { ...request, auth: false, body: outBody };
        },
      },
      {
        test: (path: string) => path.indexOf('/v1/user/login') >= 0,
        map: (database: PlatformDatabase, request: RequestData): Promise<RequestData> => {
          return Promise.resolve({ ...request, auth: false });
        },
      },
    ];
    this.customResponseModifier = [
      {
        test: (path) => isViewer(path),
        map: (
          database: PlatformDatabase,
          response: Record<string, any>,
          status: number,
        ): Promise<Record<string, any>> => {
          if (!response.isError) {
            if (response.payload === undefined) {
              return Promise.resolve(
                AppletError.createPlatformError({
                  code: response.error || status || 0,
                  status,
                  request_id: response.request_id || '',
                  message: response.message,
                  response,
                }).toJson(),
              );
            }
            const { main } = response.payload as Record<string, any>;
            // Check for main reactor error payload
            if (main && (main as Record<string, any>).error) {
              const mainError: Record<string, any> = (main as Record<string, any>).error;

              return Promise.resolve(
                AppletError.createPlatformError({
                  code: mainError.Code || status || 0,
                  status,
                  request_id: response.request_id || '',
                  message: mainError.Msg || '',
                  response: response.payload,
                }).toJson(),
              );
            }
          }
          return Promise.resolve(response);
        },
      },
      {
        test: (path) => path.indexOf('/v1/user/login') >= 0,
        map: (
          database: PlatformDatabase,
          response: Record<string, any>,
        ): Promise<Record<string, any>> => {
          if (!response.isError) {
            const responseTyped = response as { payload: Record<string, any> };
            const { token: refreshToken } = responseTyped.payload.refresh_token;
            delete responseTyped.payload.access_token;
            delete responseTyped.payload.refresh_token;
            return database.config
              .upsert({
                appId: this.appId,
                refreshToken: refreshToken ?? '',
              })
              .then(() => this.validateUserSession(database))
              .then((validation) =>
                this.bridge.sendRequest('session.updated', {
                  isValid: !!validation?.userId,
                  userId: validation?.userId ?? '',
                }),
              )
              .then(() => responseTyped);
          }
          return Promise.resolve(response);
        },
      },
      {
        test: (path: string) => isClu(path),
        map: (
          database: PlatformDatabase,
          response: Record<string, any>,
          status: number,
        ): Promise<Record<string, any>> => {
          if (!response.isError) {
            const responseTyped = response as { session?: Record<string, any> };
            if (responseTyped.session) {
              const { viewer_refresh: refreshToken } = responseTyped.session;
              delete responseTyped.session.viewer_refresh;
              if (refreshToken) {
                return database.config
                  .upsert({
                    appId: this.appId,
                    refreshToken: refreshToken ?? '',
                  })
                  .then(() => this.validateUserSession(database))
                  .then((validation) =>
                    this.bridge.sendRequest('session.updated', {
                      isValid: !!validation?.userId,
                      userId: validation?.userId ?? '',
                    }),
                  )
                  .then(() => responseTyped);
              }
            }
          }
          return Promise.resolve(response);
        },
      },
      {
        test: (path: string) => isClu(path),
        map: (
          database: PlatformDatabase,
          response: Record<string, any>,
          status: number,
        ): Promise<Record<string, any>> => {
          if (!response.isError) {
            const responseTyped = response as { url?: string };
            if (responseTyped.url) {
              const { url } = responseTyped;

              if (url.indexOf(window.location.hostname) > -1) {
                return Promise.resolve({
                  ...response,
                  url: url.replace(window.location.hostname, referrer.hostname),
                });
              }

              return Promise.resolve(response);
            }
          }
          return Promise.resolve(response);
        },
      },
    ];

    this.bridge.addRequestHandler('session.validate', async (request: RequestMessage) => {
      if (!this.isInitialized) {
        return Promise.resolve(DefaultErrors.INIT_ERROR.toJson());
      }
      return this.validateUserSession(db)
        .then((validation) => ({ isValid: !!validation?.userId, userId: validation?.userId ?? '' }))
        .catch(() => ({ isValid: false }));
    });

    this.bridge.addRequestHandler('session.clear', async (request: RequestMessage) => {
      if (!this.isInitialized) {
        return Promise.resolve(DefaultErrors.INIT_ERROR.toJson());
      }
      return this.clearSession(db).then(() => ({}));
    });

    this.bridge.addRequestHandler('request', (request: RequestMessage) => {
      if (!this.isInitialized) {
        return Promise.resolve(DefaultErrors.INIT_ERROR.toJson());
      }
      const data = request.payload as RequestData;
      if (
        this.restrictedRequestPaths.find((restricted) => data.endpoint.indexOf(restricted) >= 0) ||
        !checkAllowedDomain(data.endpoint, this.allowedRequest)
      ) {
        return Promise.resolve({
          isError: true,
          error: {
            code: 401,
            status: 401,
            requestId: '',
            message: `Unauthorized access : ${data.endpoint}`,
          },
        });
      }

      let transformedRequest = Promise.resolve(data);
      for (let i = 0; i < this.customRequestModifier.length; i += 1) {
        const handler = this.customRequestModifier[i];
        if (handler.test(data.endpoint)) {
          transformedRequest = transformedRequest.then((requestData) =>
            handler.map(db, requestData).catch((error) => {
              Log.error(error);
              return requestData;
            }),
          );
        }
      }
      return transformedRequest.then((requestData) => this.request(db, requestData));
    });

    this.bridge.addRequestHandler('websocket.open', async (request: RequestMessage) => {
      if (!this.isInitialized) {
        return Promise.resolve(DefaultErrors.INIT_ERROR.toJson());
      }
      const { appId } = this;
      if (appId) {
        const data = request.payload as {
          ws: string;
        };

        if (!checkAllowedDomain(data.ws, this.allowedWebsocket)) {
          return Promise.resolve({
            isError: true,
            error: {
              code: 401,
              status: 401,
              requestId: '',
              message: `Unauthorized access : ${data.ws}`,
            },
          });
        }

        if (!this.websockets[data.ws]) {
          const platformSocket = new PlatformSocket(appId, data.ws, () =>
            this.refreshAccessToken(db),
          );
          const subscription = platformSocket.events.subscribe((event) => {
            switch (event.type) {
              case MessageType.connect:
                this.bridge.sendRequest('websocket', { type: 'OPEN', ws: data.ws });
                break;
              case MessageType.disconnect:
                this.bridge.sendRequest('websocket', { type: 'CLOSE', ws: data.ws });
                break;
              case MessageType.message:
                this.bridge.sendRequest('websocket', {
                  type: 'MESSAGE',
                  message: event.data,
                  ws: data.ws,
                });
                break;
              case MessageType.error:
                this.bridge.sendRequest('websocket', {
                  type: 'ERROR',
                  message: event.data,
                  ws: data.ws,
                });
                break;
              default:
                break;
            }
          });
          platformSocket.connect();
          this.websockets[data.ws] = { ws: platformSocket, subscription };
        }
      }
      return Promise.resolve({});
    });

    this.bridge.addRequestHandler('websocket.close', async (request: RequestMessage) => {
      if (!this.isInitialized) {
        return Promise.resolve(DefaultErrors.INIT_ERROR.toJson());
      }
      const { appId } = this;
      if (appId) {
        const data = request.payload as {
          ws: string;
        };
        if (this.websockets[data.ws]) {
          this.websockets[data.ws].ws.close();
          this.websockets[data.ws].subscription.unsubscribe();
          delete this.websockets[data.ws];
        }
      }

      return Promise.resolve({});
    });

    const data = await this.bridge.sendRequest('init', {});

    if (data.app_id) {
      this.appId = data.app_id;
      this.platformUrl = data.platform_url;
      if (data.refresh_token) {
        await this.getAwsWafToken()
          .then((wafToken) =>
            refreshAccessToken(data.refresh_token, data.platform_url, data.app_id, wafToken),
          )
          .then(() =>
            db.config.upsert({
              appId: this.appId,
              refreshToken: data.refresh_token,
            }),
          )
          .catch(() => {
            Log.info('Invalid refresh token provided on init');
          });
      }
      this.isInitialized = true;
    } else {
      throw new Error('Missing app id or platform url');
    }

    await this.validateUserSession(db)
      .then((validation) => ({
        hasSession: !!validation?.userId,
        userId: validation?.userId ?? '',
      }))
      .then((validation) => this.bridge.sendRequest('init.complete', validation));
  }

  private validateReferrer(referrer: URL): boolean {
    if (referrer.protocol === 'https:') {
      return !!this.allowedReferrer.find((host) => {
        return referrer.hostname.endsWith(`.${host}`) || referrer.hostname === host;
      });
    }
    return false;
  }

  private async validateUserSession(db: PlatformDatabase): Promise<{ userId: string } | undefined> {
    const { appId } = this;
    if (appId) {
      const document = await db.config.findOne(appId).exec();
      const refreshToken: string | undefined = document?.get('refreshToken');
      if (refreshToken && checkJwtToken(refreshToken)) {
        try {
          const dCode: { user_id: string } = jwtDecode(refreshToken);
          return { userId: dCode.user_id };
        } catch (err) {
          return undefined;
        }
      }
    }
    return undefined;
  }

  private request(db: PlatformDatabase, data: RequestData): Promise<Record<string, any>> {
    return this.actionQueue.pushAction(() =>
      this.getAwsWafToken()
        .then((wafToken) => {
          return (
            data.auth && this.platformUrl
              ? getAccessToken(db, this.platformUrl ?? '', this.appId, wafToken)
              : Promise.resolve(undefined)
          ).then((accessToken: string | undefined) => {
            const allHeaders: Record<string, string> = {
              ...data.headers,
            };
            if (accessToken) {
              allHeaders['App-Id'] = this.appId ?? '';
              allHeaders.Authorization = `Bearer ${accessToken}`;
            }
            if (wafToken) {
              allHeaders['x-aws-waf-token'] = wafToken;
            }
            let payload: FormData | string | undefined = data.body;
            if (data.body) {
              if (!data.isFormData) {
                allHeaders['Content-Type'] = 'application/json';
              } else {
                const jsonBody: Record<string, any> = JSON.parse(data.body);
                const formData = new FormData();
                Object.keys(jsonBody).forEach((key) => {
                  if (
                    typeof jsonBody[key] === 'string' &&
                    (jsonBody[key] as string).startsWith('file:')
                  ) {
                    const parts = (jsonBody[key] as string).split(';base64,');
                    const contentParts = parts[0].replace('file:', '').split(';');
                    const byteCharacters = atob(parts[1]);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i += 1) {
                      byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: contentParts[1] });
                    formData.append(
                      key,
                      blob,
                      contentParts[0].length > 0 ? contentParts[0] : undefined,
                    );
                  } else formData.append(key, jsonBody[key]);
                });
                payload = formData;
              }
            }
            return fetch(data.endpoint, {
              method: data.method,
              headers: allHeaders,
              body: payload,
              cache: 'no-store',
            })
              .then((response) => {
                if (response.status === 405 && wafToken) {
                  throw DefaultErrors.AWS_WAF_CAPTCHA_REQUIRED;
                }
                return response.json().then((responseJson: Record<string, any>) => ({
                  json: responseJson,
                  status: response.status,
                }));
              })
              .then(({ json, status }) => {
                let transformedResponse = Promise.resolve(json);
                for (let i = 0; i < this.customResponseModifier.length; i += 1) {
                  const handler = this.customResponseModifier[i];
                  if (handler.test(data.endpoint)) {
                    transformedResponse = transformedResponse.then((response) =>
                      handler.map(db, response, status).catch((error) => {
                        Log.error(error);
                        return response;
                      }),
                    );
                  }
                }
                return transformedResponse;
              });
          });
        })
        .catch((error) => {
          if (error === DefaultErrors.AWS_WAF_CAPTCHA_REQUIRED) {
            this.actionQueue.pause();
            return this.requestWafCaptcha().then(() => {
              this.actionQueue.resume();
              return this.request(db, data);
            });
          }
          if (error === DefaultErrors.INVALID_USER_SESSION) {
            this.clearSession(db);
          }
          throw error;
        }),
    ) as Promise<Record<string, any>>;
  }

  private getAwsWafToken(): Promise<string | undefined> {
    return this.bridge
      .sendRequest('waf.token.get', {})
      .then((result) => result.token as string | undefined);
  }

  private requestWafCaptcha(): Promise<void> {
    return this.bridge.sendRequest('waf.captcha.show', {}).then(() => {});
  }

  private async clearSession(db: PlatformDatabase): Promise<void> {
    const { appId } = this;
    if (appId) {
      await db.config.findOne(appId).remove();
      Object.keys(this.websockets).forEach((key) => {
        this.websockets[key].ws.close();
        this.websockets[key].subscription.unsubscribe();
        delete this.websockets[key];
      });
    }
    this.bridge.sendRequest('session.updated', {
      isValid: false,
      userId: '',
    });
  }

  private refreshAccessToken(db: PlatformDatabase): Promise<string> {
    return this.actionQueue.pushAction(() =>
      this.getAwsWafToken()
        .then((wafToken) => getAccessToken(db, this.platformUrl ?? '', this.appId, wafToken))
        .catch((error) => {
          if (error === DefaultErrors.AWS_WAF_CAPTCHA_REQUIRED) {
            this.actionQueue.pause();
            return this.requestWafCaptcha().then(() => {
              this.actionQueue.resume();
              return this.refreshAccessToken(db);
            });
          }
          throw error;
        }),
    ) as Promise<string>;
  }
}

// eslint-disable-next-line no-new
new Requestor();
